import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Button } from './ui/button';
import { 
  X, ChevronRight, ChevronLeft, Sparkles, 
  Target, BarChart3, Wallet, Bot, CheckCircle2,
  ArrowRight, SkipForward, Settings, TrendingUp, 
  CreditCard, DollarSign, Landmark, ClipboardList, Zap
} from 'lucide-react';

const OnboardingTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isOnboardingActive,
    currentStep,
    currentStepData,
    totalSteps,
    progress,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding
  } = useOnboarding();

  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetElement, setTargetElement] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Icons for each step
  const stepIcons = {
    welcome: <Target className="w-8 h-8" />,
    hub: <Sparkles className="w-6 h-6" />,
    dashboard: <BarChart3 className="w-6 h-6" />,
    faturamento: <Wallet className="w-6 h-6" />,
    parcelamento: <Zap className="w-6 h-6" />,
    receita: <DollarSign className="w-6 h-6" />,
    despesas: <ClipboardList className="w-6 h-6" />,
    caixa: <Landmark className="w-6 h-6" />,
    categorias: <Settings className="w-6 h-6" />,
    compromissos: <CreditCard className="w-6 h-6" />,
    projecao: <TrendingUp className="w-6 h-6" />,
    saude: <BarChart3 className="w-6 h-6" />,
    capacidade: <DollarSign className="w-6 h-6" />,
    diretor: <Bot className="w-6 h-6" />,
    complete: <CheckCircle2 className="w-8 h-8" />
  };

  // Navigate to the correct route for current step
  useEffect(() => {
    if (isOnboardingActive && currentStepData?.route) {
      if (location.pathname !== currentStepData.route) {
        navigate(currentStepData.route);
      }
    }
  }, [isOnboardingActive, currentStepData, location.pathname, navigate]);

  // Find and position tooltip relative to target element
  const updateTooltipPosition = useCallback(() => {
    if (!currentStepData?.target) {
      setTargetElement(null);
      return;
    }

    const element = document.querySelector(currentStepData.target);
    if (element) {
      setTargetElement(element);
      const rect = element.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      let top, left;
      const isMobile = window.innerWidth < 640;
      const tooltipWidth = isMobile ? Math.min(320, window.innerWidth - 32) : 320;
      const tooltipHeight = 220; // Account for content + buttons
      const padding = 16;

      // Keep tooltip in viewport - calculate bounds first
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const maxTop = scrollTop + viewportHeight - tooltipHeight - padding - 50; // 50px for step dots
      const minTop = scrollTop + padding;

      // On mobile, always center horizontally and position above or below with more care
      if (isMobile) {
        left = (viewportWidth - tooltipWidth) / 2;
        // Try to position above the target first, if no space, below
        const spaceAbove = rect.top - padding - tooltipHeight;
        const spaceBelow = viewportHeight - rect.bottom - padding - tooltipHeight - 50;
        
        if (spaceAbove > 0) {
          top = rect.top + scrollTop - tooltipHeight - padding;
        } else if (spaceBelow > 0) {
          top = rect.bottom + scrollTop + padding;
        } else {
          // Not enough space above or below, center vertically
          top = scrollTop + (viewportHeight - tooltipHeight) / 2 - 25;
        }
      } else {
        switch (currentStepData.position) {
          case 'top':
            top = rect.top + scrollTop - tooltipHeight - padding;
            left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'bottom':
            top = rect.bottom + scrollTop + padding;
            left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'left':
            top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.left + scrollLeft - tooltipWidth - padding;
            break;
          case 'right':
            top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.right + scrollLeft + padding;
            break;
          default:
            top = rect.bottom + scrollTop + padding;
            left = rect.left + scrollLeft;
        }
      }

      // Final clamp to viewport
      if (left < padding) left = padding;
      if (left + tooltipWidth > viewportWidth - padding) {
        left = viewportWidth - tooltipWidth - padding;
      }
      if (top < minTop) top = minTop;
      if (top > maxTop) top = maxTop;

      setTooltipPosition({ top, left });

      // Scroll element into view if needed (only on desktop)
      if (!isMobile) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setTargetElement(null);
    }
  }, [currentStepData]);

  useEffect(() => {
    if (isOnboardingActive) {
      // Small delay to ensure DOM is ready after navigation
      const timer = setTimeout(() => {
        updateTooltipPosition();
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 300);
      }, 300);

      window.addEventListener('resize', updateTooltipPosition);
      window.addEventListener('scroll', updateTooltipPosition);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateTooltipPosition);
        window.removeEventListener('scroll', updateTooltipPosition);
      };
    }
  }, [isOnboardingActive, currentStep, updateTooltipPosition]);

  if (!isOnboardingActive) return null;

  // Render description with line breaks (\n), bullets (• ) and **bold**
  const renderInline = (s) => s.split('**').map((part, idx) =>
    idx % 2 === 1
      ? <strong key={idx} className="text-zinc-100 font-semibold">{part}</strong>
      : <React.Fragment key={idx}>{part}</React.Fragment>
  );
  const renderDescription = (text) => {
    const lines = (text || '').split('\n').map(l => l.trim()).filter(Boolean);
    return lines.map((line, i) => {
      if (line.startsWith('• ')) {
        return (
          <div key={i} className="flex gap-2 items-start">
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            <span className="flex-1">{renderInline(line.slice(2))}</span>
          </div>
        );
      }
      return <p key={i}>{renderInline(line)}</p>;
    });
  };
  const descHasBullets = (currentStepData?.description || '').includes('\n');

  const isModalStep = !currentStepData?.target;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // Modal-style step (welcome and complete)
  if (isModalStep) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={skipOnboarding}
        />
        
        {/* Modal */}
        <div className={`relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl p-6 shadow-2xl transform transition-all duration-300 ${isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
          {/* Close button */}
          <button
            onClick={skipOnboarding}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`p-4 rounded-full ${isLastStep ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
              {stepIcons[currentStepData.id]}
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-3 text-center">
              {currentStepData.title}
            </h2>
            <div className={`text-zinc-400 text-sm leading-relaxed space-y-2 max-h-[46vh] overflow-y-auto pr-1 ${descHasBullets ? 'text-left' : 'text-center'}`}>
              {renderDescription(currentStepData.description)}
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-zinc-500 mb-2">
              <span>Passo {currentStep + 1} de {totalSteps}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={prevStep}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <ChevronLeft size={18} className="mr-1" />
                Voltar
              </Button>
            )}
            
            {isLastStep ? (
              <Button
                onClick={completeOnboarding}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Começar a Usar
                <CheckCircle2 size={18} className="ml-2" />
              </Button>
            ) : (
              <>
                <Button
                  onClick={nextStep}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isFirstStep ? 'Iniciar Tour' : 'Próximo'}
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              </>
            )}
          </div>

          {/* Skip option */}
          {!isLastStep && (
            <button
              onClick={skipOnboarding}
              className="w-full mt-4 text-sm text-zinc-500 hover:text-zinc-300 transition-colors flex items-center justify-center gap-1"
            >
              <SkipForward size={14} />
              Pular tutorial
            </button>
          )}
        </div>
      </div>
    );
  }

  // Tooltip-style step with target highlight
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      {/* Backdrop with hole for target */}
      <div className="absolute inset-0 pointer-events-auto">
        <svg className="w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetElement && (
                <rect
                  x={targetElement.getBoundingClientRect().left - 8}
                  y={targetElement.getBoundingClientRect().top - 8}
                  width={targetElement.getBoundingClientRect().width + 16}
                  height={targetElement.getBoundingClientRect().height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect 
            width="100%" 
            height="100%" 
            fill="rgba(0,0,0,0.75)" 
            mask="url(#spotlight-mask)"
            onClick={skipOnboarding}
          />
        </svg>
      </div>

      {/* Highlight ring around target */}
      {targetElement && currentStepData.highlight && (
        <div
          className="absolute border-2 border-emerald-500 rounded-xl pointer-events-none animate-pulse"
          style={{
            left: targetElement.getBoundingClientRect().left - 8,
            top: targetElement.getBoundingClientRect().top - 8,
            width: targetElement.getBoundingClientRect().width + 16,
            height: targetElement.getBoundingClientRect().height + 16,
            boxShadow: '0 0 20px rgba(34, 197, 94, 0.5)'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className={`absolute w-80 bg-zinc-900 border border-zinc-700 rounded-xl p-5 shadow-2xl pointer-events-auto transform transition-all duration-300 ${isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          maxWidth: 'calc(100vw - 32px)'
        }}
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400">
              {stepIcons[currentStepData.id]}
            </div>
            <span className="text-xs text-zinc-500">
              {currentStep + 1}/{totalSteps}
            </span>
          </div>
          <button
            onClick={skipOnboarding}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold text-white mb-2">
          {currentStepData.title}
        </h3>
        <div className="text-sm text-zinc-400 leading-relaxed space-y-1.5 mb-4 max-h-[40vh] overflow-y-auto pr-1">
          {renderDescription(currentStepData.description)}
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden mb-4">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Navigation */}
        <div className="flex gap-2">
          {!isFirstStep && (
            <Button
              variant="outline"
              size="sm"
              onClick={prevStep}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <ChevronLeft size={16} />
            </Button>
          )}
          
          <Button
            size="sm"
            onClick={nextStep}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLastStep ? 'Finalizar' : 'Próximo'}
            <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>

      {/* Step dots */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-auto">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep 
                ? 'bg-emerald-500 w-6' 
                : index < currentStep 
                  ? 'bg-emerald-500/50' 
                  : 'bg-zinc-600'
            }`}
            onClick={() => {}}
          />
        ))}
      </div>
    </div>
  );
};

export default OnboardingTour;
